import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Header } from "@/components/Header";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  checkIsAdmin,
  createAccessGrant,
  listAccessGrants,
  revokeAccessGrant,
} from "@/lib/access-grants.functions";

export const Route = createFileRoute("/admin/grants")({
  head: () => ({
    meta: [
      { title: "Codes d'accès — Admin" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AdminGrantsPage,
});

function AdminGrantsPage() {
  const nav = useNavigate();
  const checkFn = useServerFn(checkIsAdmin);
  const listFn = useServerFn(listAccessGrants);
  const createFn = useServerFn(createAccessGrant);
  const revokeFn = useServerFn(revokeAccessGrant);
  const qc = useQueryClient();
  const [authed, setAuthed] = useState(false);
  const [admin, setAdmin] = useState<boolean | null>(null);

  const [tier, setTier] = useState<"premium" | "pro">("premium");
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data }) => {
      if (!data.session) {
        nav({ to: "/login" });
        return;
      }
      setAuthed(true);
      const r = await checkFn({ data: undefined as any });
      setAdmin(r.isAdmin);
    });
  }, [checkFn, nav]);

  const { data } = useQuery({
    queryKey: ["admin-grants"],
    queryFn: () => listFn({ data: undefined as any }),
    enabled: authed && admin === true,
  });

  const createMut = useMutation({
    mutationFn: async () =>
      createFn({
        data: {
          tier,
          beneficiary_email: email.trim() || undefined,
          note: note.trim() || undefined,
        },
      }),
    onSuccess: (row: any) => {
      qc.invalidateQueries({ queryKey: ["admin-grants"] });
      navigator.clipboard?.writeText(row.code).catch(() => {});
      if (row.emailed === "sent") {
        toast.success(`Code créé et envoyé par email : ${row.code}`);
      } else if (row.emailed === "failed") {
        toast.warning(`Code créé (${row.code}) mais l'email n'a pas pu être envoyé.`);
      } else {
        toast.success(`Code créé et copié : ${row.code}`);
      }
      setEmail("");
      setNote("");
    },
    onError: (e: any) => toast.error(e?.message ?? "Erreur"),
  });

  const revokeMut = useMutation({
    mutationFn: async (id: string) => revokeFn({ data: { id } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-grants"] });
      toast.success("Code révoqué");
    },
    onError: (e: any) => toast.error(e?.message ?? "Erreur"),
  });

  if (admin === false) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <Header />
        <div className="max-w-2xl mx-auto px-6 py-20 text-center">
          <h1 className="font-display font-bold text-2xl mb-2">Accès refusé</h1>
          <p className="text-sm text-muted-foreground">Vous n'êtes pas administrateur.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <section className="max-w-4xl mx-auto px-6 py-16">
        <h1 className="font-display font-bold text-3xl mb-2 tracking-tight">
          Codes d'accès gratuits
        </h1>
        <p className="text-muted-foreground mb-8 text-sm">
          Générez des codes nominatifs (usage unique) qui débloquent Premium ou Pro sans Stripe.
        </p>

        <div className="bg-card border border-foreground/10 rounded-3xl p-6 mb-10">
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">
            Nouveau code
          </p>
          <div className="grid sm:grid-cols-3 gap-3 mb-3">
            <select
              value={tier}
              onChange={(e) => setTier(e.target.value as any)}
              className="rounded-xl border border-foreground/15 bg-background px-3 py-2 text-sm"
            >
              <option value="premium">Premium</option>
              <option value="pro">Pro</option>
            </select>
            <input
              type="email"
              placeholder="email (optionnel)"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded-xl border border-foreground/15 bg-background px-3 py-2 text-sm sm:col-span-2"
            />
          </div>
          <input
            type="text"
            placeholder="Note interne (ex: VIP test, partenaire)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="w-full rounded-xl border border-foreground/15 bg-background px-3 py-2 text-sm mb-3"
          />
          <button
            onClick={() => createMut.mutate()}
            disabled={createMut.isPending}
            className="bg-foreground text-background px-4 py-2 rounded-xl text-sm font-semibold hover:opacity-90 disabled:opacity-50"
          >
            {createMut.isPending ? "…" : "Créer le code"}
          </button>
        </div>

        <div className="bg-card border border-foreground/10 rounded-3xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left p-3">Code</th>
                <th className="text-left p-3">Tier</th>
                <th className="text-left p-3">Bénéficiaire</th>
                <th className="text-left p-3">Statut</th>
                <th className="text-left p-3">Utilisé le</th>
                <th className="text-left p-3">Dernière utilisation</th>
                <th className="text-left p-3">Note</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {(data?.items ?? []).map((g: any) => {
                const isRedeemed = !!g.redeemed_at || !!g.beneficiary_user_id;
                const isRevoked = !g.active || !!g.revoked_at;
                return (
                <tr key={g.id} className="border-t border-foreground/10">
                  <td className="p-3 font-mono text-xs flex items-center gap-2">
                    <span>{g.code}</span>
                    <button
                      onClick={() => {
                        navigator.clipboard?.writeText(g.code).catch(() => {});
                        toast.success("Code copié");
                      }}
                      className="text-[10px] text-muted-foreground hover:text-foreground underline"
                      title="Copier"
                    >
                      copier
                    </button>
                  </td>
                  <td className="p-3">{g.tier}</td>
                  <td className="p-3 text-xs">
                    {g.beneficiary_email ?? "—"}
                    {g.beneficiary_user_id && (
                      <span
                        className="block text-[10px] text-muted-foreground font-mono truncate max-w-[160px]"
                        title={g.beneficiary_user_id}
                      >
                        uid: {String(g.beneficiary_user_id).slice(0, 8)}…
                      </span>
                    )}
                  </td>
                  <td className="p-3">
                    {isRevoked ? (
                      <span className="text-red-500 text-xs font-bold">Révoqué</span>
                    ) : isRedeemed ? (
                      <span className="text-blue-600 text-xs font-bold">Utilisé</span>
                    ) : (
                      <span className="text-green-600 text-xs font-bold">Actif</span>
                    )}
                  </td>
                  <td className="p-3 text-xs text-muted-foreground">
                    {g.redeemed_at
                      ? new Date(g.redeemed_at).toLocaleString()
                      : "—"}
                  </td>
                  <td className="p-3 text-xs text-muted-foreground">
                    {g.last_used_at
                      ? new Date(g.last_used_at).toLocaleString()
                      : "—"}
                  </td>
                  <td className="p-3 text-xs text-muted-foreground">{g.note ?? ""}</td>
                  <td className="p-3 text-right">
                    {g.active && !g.revoked_at && (
                      <button
                        onClick={() => revokeMut.mutate(g.id)}
                        className="text-xs text-red-500 hover:underline"
                      >
                        Révoquer
                      </button>
                    )}
                  </td>
                </tr>
                );
              })}
              {data?.items?.length === 0 && (
                <tr>
                  <td colSpan={8} className="p-6 text-center text-muted-foreground text-xs">
                    Aucun code pour l'instant.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}